// LxSelect — Lamtix-token-wrapped Select primitive
//
// RTL audit:
//   - Trigger text uses text-start (logical, RTL-safe)
//   - Chevron uses ms-auto (pushed to inline-end in both LTR and RTL)
//   - Items use ps-8 / pe-2 (logical padding — start/end)
//   - Checkmark at start-2 (inline-start, flips in RTL)
//   - Dropdown does not clip right edge in RTL (Radix Portal handles positioning)
//   - No raw Tailwind colors — all semantic
//
// D-SHADCN-LAMTIX-WRAPPED-01: sole import path is @lamtix/ui
import * as React from 'react';
import { cn } from '../utils';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../shadcn/select';

// Re-export sub-primitives with Lx prefix for barrel export
const LxSelectGroup = SelectGroup;
const LxSelectValue = SelectValue;
const LxSelectSeparator = SelectSeparator;
const LxSelectLabel = SelectLabel;

// ── LxSelect ─────────────────────────────────────────────────────────────────

export interface LxSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface LxSelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  options?: LxSelectOption[];
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

function LxSelect({
  value,
  defaultValue,
  onValueChange,
  placeholder,
  options,
  disabled,
  className,
  children,
}: LxSelectProps) {
  // exactOptionalPropertyTypes: conditionally spread to avoid passing undefined
  const selectProps = {
    ...(value !== undefined && { value }),
    ...(defaultValue !== undefined && { defaultValue }),
    ...(onValueChange !== undefined && { onValueChange }),
    disabled: disabled ?? false,
  };

  return (
    <Select {...selectProps}>
      <SelectTrigger
        className={cn(
          // text-start: RTL-safe text alignment on trigger
          'text-start w-full min-h-[44px] border-border bg-background text-foreground',
          'focus:ring-ring focus:border-ring',
          className,
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="text-start border-border bg-popover text-popover-foreground">
        {options
          ? options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled ?? false}>
              {opt.label}
            </SelectItem>
          ))
          : children}
      </SelectContent>
    </Select>
  );
}

// ── LxSelectItem re-export ────────────────────────────────────────────────────

const LxSelectItem = React.forwardRef<
  React.ElementRef<typeof SelectItem>,
  React.ComponentPropsWithoutRef<typeof SelectItem>
>(({ className, ...props }, ref) => (
  <SelectItem
    ref={ref}
    className={cn('text-start cursor-pointer', className)}
    {...props}
  />
));
LxSelectItem.displayName = 'LxSelectItem';

// ── LxSelectTrigger re-export ────────────────────────────────────────────────

const LxSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectTrigger>,
  React.ComponentPropsWithoutRef<typeof SelectTrigger>
>(({ className, ...props }, ref) => (
  <SelectTrigger
    ref={ref}
    className={cn('text-start min-h-[44px] border-border bg-background text-foreground', className)}
    {...props}
  />
));
LxSelectTrigger.displayName = 'LxSelectTrigger';

// ── LxSelectContent re-export ─────────────────────────────────────────────────

const LxSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectContent>,
  React.ComponentPropsWithoutRef<typeof SelectContent>
>(({ className, ...props }, ref) => (
  <SelectContent
    ref={ref}
    className={cn('text-start border-border bg-popover', className)}
    {...props}
  />
));
LxSelectContent.displayName = 'LxSelectContent';

export {
  LxSelect,
  LxSelectItem,
  LxSelectTrigger,
  LxSelectContent,
  LxSelectGroup,
  LxSelectValue,
  LxSelectSeparator,
  LxSelectLabel,
  // Raw Radix root for advanced composition
  Select as LxSelectRoot,
};
